import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  downloadFromBlob,
  extractFilenameFromContentDisposition,
} from "@/utils/downloadUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

function axiosHeaderString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

export interface FileDownloadOptions {
  filename?: string;
  mimeType?: string;
}

export function useFileDownload() {
  const { globalErrorHandler } = useGlobalHandlers();

  /**
   * Downloads a company document using the upload ID
   */
  const downloadCompanyDocument = async (
    companyId: number,
    uploadId: number,
    options?: FileDownloadOptions
  ) => {
    try {
      const response = await goSolidApi.get(
        `/api/company/${companyId}/download/${uploadId}`,
        {
          responseType: "blob",
        }
      );

      if (response.status === 200) {
        let filename = options?.filename || `Document_${uploadId}`;

        // Extract filename from Content-Disposition header if available
        const extractedFilename = extractFilenameFromContentDisposition(
          axiosHeaderString(response.headers["content-disposition"])
        );
        if (extractedFilename) {
          filename = extractedFilename;
        }

        const mimeType =
          options?.mimeType ||
          axiosHeaderString(response.headers["content-type"]);
        downloadFromBlob(response.data, filename, mimeType);
      } else {
        throw new Error("Error downloading the document");
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  };

  /**
   * Downloads a document using a token (for Dokobit/signed documents)
   */
  const downloadDocumentByToken = async (token: string) => {
    try {
      const response = await goSolidApi.get(`/api/document-download/${token}`, {
        responseType: "blob",
      });

      if (response.status === 200) {
        let filename = "DownloadedDocument";

        // Extract filename from Content-Disposition header if available
        const extractedFilename = extractFilenameFromContentDisposition(
          axiosHeaderString(response.headers["content-disposition"])
        );
        if (extractedFilename) {
          filename = extractedFilename;
        }

        const mimeType = axiosHeaderString(response.headers["content-type"]);
        downloadFromBlob(response.data, filename, mimeType);
      } else {
        throw new Error("Error downloading the document");
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  };

  /**
   * Downloads an invoice by ID. Returns a single PDF or ZIP bundle depending on associated invoices.
   */
  const downloadInvoice = async (invoiceId: number) => {
    try {
      const response = await goSolidApi.get(
        `/api/download-invoice/${invoiceId}`,
        { responseType: "blob" }
      );

      if (response.status === 200) {
        const contentType =
          axiosHeaderString(response.headers["content-type"]) ?? "";
        const isZip = contentType.includes("zip");
        const ext = isZip ? "zip" : "pdf";
        const mime = isZip ? "application/zip" : "application/pdf";
        downloadFromBlob(response.data, `invoice_${invoiceId}.${ext}`, mime);
      } else {
        throw new Error("Error downloading the invoice");
      }
    } catch (error: unknown) {
      globalErrorHandler(error);
    }
  };

  return {
    downloadCompanyDocument,
    downloadDocumentByToken,
    downloadInvoice,
  };
}
